import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { motion } from "framer-motion";
import { HelpCircle, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { business } from "@/lib/business-data";

export const faqs = [
  {
    q: "What areas do you serve?",
    a: "We work out of Hayward and cover a 30-mile radius across the East Bay, Tri-Valley, and Peninsula — including Castro Valley, San Leandro, Union City, Fremont, Oakland, Berkeley, Dublin, San Ramon, Livermore, Danville, Walnut Creek, San Francisco, San Mateo, and Palo Alto. If you're not sure whether your address is covered, just ask when you call.",
  },
  {
    q: "Are estimates really free?",
    a: "Yes. We walk the job, talk through what you want done, and send a written scope with pricing before anything is committed. There's no charge for the visit and no obligation to book.",
  },
  {
    q: "How quickly can you start?",
    a: "Smaller repairs can often be scheduled within the same week, particularly in Hayward, Castro Valley, San Leandro, and Union City. Larger projects like bathroom remodels are scheduled around material lead times, which we confirm in the estimate.",
  },
  {
    q: "Do you handle small jobs, or only large projects?",
    a: "Both. A single sticking door, one wall patch, or a TV that needs mounting is perfectly normal work for us. In fact, batching several small items into one visit is usually the most cost-effective way to get a list cleared.",
  },
  {
    q: "Do you speak Spanish?",
    a: `Yes — ${business.name} is owned and run by ${business.owner}, and we work in both English and Spanish. Estimates, scope discussions, and questions during the job can all happen in whichever you prefer.`,
  },
  {
    q: "What are your hours?",
    a: "Monday through Saturday, 7:00 AM to 7:00 PM. We're closed Sundays. Early starts are common and easy to arrange — just mention the time that works when booking.",
  },
  {
    q: "Is there a discount for repeat customers?",
    a: "There is. Returning customers get 10% off their next project, and if you refer someone who books with us, you get $50 back once their job is complete. Just mention it when you call — there's no code or coupon to track down.",
  },
  {
    q: "What kind of work is outside what you do?",
    a: "Anything that changes a building system rather than a component: new electrical circuits and panel work, repiping and sewer lateral work, gas line changes, structural alterations, and full HVAC system replacement. Those need a licensed specialist, and we'll tell you plainly rather than take work we shouldn't.",
  },
];

const FAQSection = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section id="faq" className="section-padding section-tint">
      <div className="container-max max-w-3xl">
        <div className="text-center mb-14">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="eyebrow-pill mb-5"
          >
            <HelpCircle className="h-3 w-3" /> FAQ
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-foreground tracking-tight font-bold leading-[1.05]"
            style={{ fontSize: "clamp(2.25rem, 4.5vw, 4rem)" }}
          >
            Frequently Asked <span className="text-gradient-primary">Questions</span>
          </motion.h2>
        </div>

        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((faq, i) => (
            <motion.div
              key={faq.q}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: Math.min(i, 4) * 0.05, duration: 0.4 }}
            >
              <AccordionItem
                value={`faq-${i}`}
                className="bg-card rounded-xl border border-border px-5 sm:px-6 data-[state=open]:shadow-md data-[state=open]:border-primary/30 transition-all"
              >
                <AccordionTrigger className="font-heading text-base sm:text-lg tracking-tight text-foreground hover:no-underline py-5 text-left font-bold">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-foreground/75 text-sm sm:text-[15px] leading-relaxed pb-5 text-left">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            </motion.div>
          ))}
        </Accordion>

        <div className="mt-12 rounded-2xl border border-primary/25 bg-primary/5 p-6 sm:p-8 text-center">
          <h3 className="font-heading text-xl sm:text-2xl font-bold text-foreground mb-2">
            Still have a question?
          </h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Call and describe the job — we'll tell you honestly whether it's something we should
            take on.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Button size="lg" onClick={openQuoteForm} className="gap-2 w-full sm:w-auto">
              Get Free Estimate <ArrowRight className="h-4 w-4" />
            </Button>
            <Button asChild size="lg" variant="outline" className="gap-2 w-full sm:w-auto">
              <a href={business.phoneHref}>
                <Phone className="h-4 w-4 text-primary" /> {business.phone}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
